import { useEffect, useState } from "react";
import { Card, SectionTitle, TornaHome } from "@/components/Kpi";
import { MonitoraggioQuarter } from "@/components/MonitoraggioQuarter";
import { api } from "@/lib/api";
import { eur, eurShort, num } from "@/lib/format";

const SETTORI = {
  corto: { etichetta: "Canale corto" },
  lungo: { etichetta: "Canale lungo" },
  totale: { etichetta: "Totale DBE" },
};

// tonalità a scalare: il totale di ogni canale è il più marcato, le componenti via via più chiare
const STILI = {
  corto: { fondo: "#DBE6FF", bordo: "#1E3A8A" },
  non_garantito: { fondo: "#E7EEFF", bordo: "#2563EB" },
  vivibanca: { fondo: "#F1F6FF", bordo: "#60A5FA" },
  lungo: { fondo: "#FFE7D1", bordo: "#B45309" },
  auto: { fondo: "#FFF0DF", bordo: "#EA8C0B" },
  pos: { fondo: "#FFF8EF", bordo: "#FBBF24" },
  totale: { fondo: "#DCFAE6", bordo: "#15803D" },
};

const COLONNE = [
  { key: "chiusura_2025", titolo: "Chiusura 2025", tipo: "eur" },
  { key: "budget", titolo: "Budget 2026", tipo: "eur" },
  { key: "budget_vs_2025_perc", titolo: "Δ% bdg / 2025", tipo: "perc" },
  { key: "budget_vs_2025_eur", titolo: "Δ€ bdg / 2025", tipo: "eurDelta" },
  { key: "proiezione", titolo: "Proiezione 2026", tipo: "eur", forte: true },
  { key: "proiezione_vs_2025_perc", titolo: "Δ% proiez. / 2025", tipo: "perc" },
  { key: "proiezione_vs_2025_eur", titolo: "Δ€ proiez. / 2025", tipo: "eurDelta" },
  { key: "proiezione_vs_budget_perc", titolo: "Δ% proiez. / bdg", tipo: "perc" },
  { key: "proiezione_vs_budget_eur", titolo: "Δ€ proiez. / bdg", tipo: "eurDelta" },
];

const segno = (v) => (v > 0 ? "+" : "");
const tinta = (v) => (v === null || v === undefined ? "text-slate-400"
  : v >= 0 ? "text-emerald-700" : "text-red-600");

const soldi = (v) => `${segno(v)}${eurShort(v)}`;

function Valore({ tipo, v, forte }) {
  if (v === null || v === undefined) return <span className="text-slate-300">—</span>;
  if (tipo === "eur") {
    return (
      <span className={forte ? "text-[17px] font-bold text-slate-900" : "text-[15px] text-slate-800"}>
        {eurShort(v)}
      </span>
    );
  }
  if (tipo === "perc") {
    return <span className={`text-[15px] font-bold ${tinta(v)}`}>{segno(v)}{num(v, 2)}%</span>;
  }
  return <span className={`text-[15px] font-semibold ${tinta(v)}`}>{soldi(v)}</span>;
}

export default function Monitoraggio() {
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState("");

  useEffect(() => {
    api.get("/monitoraggio").then((r) => setDati(r.data))
      .catch(() => setErrore("Monitoraggio non disponibile"));
  }, []);

  if (errore) return <Card className="p-8 text-sm text-slate-500" data-testid="monitoraggio-errore">{errore}</Card>;
  if (!dati) return <div className="text-sm text-slate-500" data-testid="monitoraggio-loading">Caricamento…</div>;

  const primaDelSettore = (i) => i === 0 || dati.righe[i - 1].settore !== dati.righe[i].settore;

  return (
    <div className="space-y-4 -mt-3" data-testid="monitoraggio-page">
      <TornaHome testId="btn-monitoraggio-torna-home" />
      <SectionTitle>
        Monitoraggio DBE {dati.anno}
      </SectionTitle>

      <div className="hidden lg:block">
        <Card className="p-5" data-testid="monitoraggio-tabella">
          <table className="w-full table-fixed border-separate" style={{ borderSpacing: "4px 6px" }}>
            <thead>
              <tr>
                <th className="w-[15%]" />
                {COLONNE.map((c) => (
                  <th key={c.key}
                    className="px-2 py-2 rounded-md bg-slate-100 text-[11px] font-bold uppercase
                      tracking-[0.05em] text-slate-600 text-right align-bottom leading-tight">
                    {c.titolo}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dati.righe.map((r, i) => {
                const s = { ...SETTORI[r.settore], ...STILI[r.key] };
                return (
                  <tr key={r.key} data-testid={`monitoraggio-riga-${r.key}`}
                    className={primaDelSettore(i) ? "border-t-0" : ""}>
                    <td className="px-3 py-3 rounded-md font-black text-[13px] uppercase
                      tracking-normal text-slate-800 border-l-4 leading-tight"
                      style={{ background: s.fondo, borderColor: s.bordo }}>
                      {primaDelSettore(i) && (
                        <span className="block text-[9px] font-bold tracking-[0.14em] mb-0.5"
                          style={{ color: s.bordo }}>{s.etichetta}</span>
                      )}
                      {r.etichetta}
                    </td>
                    {COLONNE.map((c) => (
                      <td key={c.key}
                        className="px-2 py-3 rounded-md text-right tabular-nums font-mono
                          whitespace-nowrap ring-1 ring-slate-200/70"
                        style={{ background: s.fondo }}
                        data-testid={`monitoraggio-${r.key}-${c.key}`}>
                        <Valore tipo={c.tipo} v={r[c.key]} forte={c.forte} />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:hidden">
        {dati.righe.map((r) => (
          <Card key={r.key} className="p-5 border-l-4"
            style={{ borderColor: STILI[r.key].bordo, background: STILI[r.key].fondo }}
            data-testid={`monitoraggio-card-${r.key}`}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-bold uppercase tracking-wide text-slate-800">{r.etichetta}</span>
              <span className="font-mono text-base font-bold text-slate-900">{eur(r.proiezione)}</span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-xs font-mono">
              {COLONNE.filter((c) => c.key !== "proiezione").map((c) => (
                <div key={c.key} className="flex items-baseline justify-between gap-2">
                  <span className="font-sans text-slate-500">{c.titolo}</span>
                  <Valore tipo={c.tipo} v={r[c.key]} />
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <MonitoraggioQuarter />
    </div>
  );
}
